<script setup lang="ts">
import { computed, inject, ref, type Ref } from 'vue'
import { useFormControl } from '../../composables/useFormControl'
import { useBoundValue } from '../../composables/useBoundValue'
export type CheckboxItemValue = string | number | boolean
export interface CheckboxGroupContext {
  value: Ref<CheckboxItemValue[]>
  disabled: Ref<boolean>
  name: Ref<string | undefined>
  onChange: (value: CheckboxItemValue[]) => void
}
defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<{
  checked?: boolean
  defaultChecked?: boolean
  disabled?: boolean
  indeterminate?: boolean
  value?: CheckboxItemValue
}>(), { checked: undefined, defaultChecked: false, disabled: undefined, indeterminate: false })
const emit = defineEmits<{
  'update:checked': [checked: boolean]
  change: [event: Event]
}>()
const { attrs, disabled, controlAttrs, onFieldChange, onFieldBlur } = useFormControl(props)
const group = inject<CheckboxGroupContext | null>('rrCheckboxGroupContext', null)
const inputRef = ref<HTMLInputElement | null>(null)
const innerChecked = ref(props.defaultChecked)
const mergedChecked = useBoundValue(props, 'checked', innerChecked)
const isChecked = computed(() => group && props.value !== undefined ? group.value.value.includes(props.value) : Boolean(mergedChecked.value))
const isDisabled = computed(() => disabled.value || Boolean(group?.disabled.value))
function handleChange(event: Event) {
  if (isDisabled.value) return
  const checked = (event.target as HTMLInputElement).checked
  if (group && props.value !== undefined) {
    const next = group.value.value.filter(value => value !== props.value)
    if (checked) next.push(props.value)
    group.onChange(next)
  } else {
    innerChecked.value = checked
    emit('update:checked', checked)
    onFieldChange()
  }
  emit('change', event)
}
defineExpose({ focus: (options?: FocusOptions) => inputRef.value?.focus(options), blur: () => inputRef.value?.blur() })
</script>

<template>
  <label
    class="rr-checkbox-wrapper"
    :style="attrs.style as any"
    :class="[attrs.class, {
      'is-disabled': isDisabled,
      'is-checked': isChecked,
      'is-indeterminate': indeterminate
    }]"
  >
    <span class="rr-checkbox">
      <input
        ref="inputRef"
        v-bind="{ ...controlAttrs(), ...(group ? { id: undefined, name: group.name.value } : {}) }"
        type="checkbox"
        :checked="isChecked"
        :disabled="isDisabled"
        :indeterminate="indeterminate"
        :value="value"
        class="rr-checkbox__input"
        @change="handleChange"
        @blur="onFieldBlur"
      />
      <span class="rr-checkbox__inner" />
    </span>
    <span v-if="$slots.default" class="rr-checkbox__label">
      <slot />
    </span>
  </label>
</template>

<style scoped>
.rr-checkbox-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--rr-color-text);
  font-size: 13px;
  cursor: pointer;
  user-select: none;
  line-height: 1;
}

.rr-checkbox-wrapper.is-disabled {
  color: var(--rr-color-text-subtle);
  cursor: not-allowed;
}

.rr-checkbox {
  position: relative;
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.rr-checkbox__input {
  position: absolute;
  inset: 0;
  opacity: 0;
  margin: 0;
  cursor: pointer;
}

.rr-checkbox__inner {
  display: inline-block;
  width: 16px;
  height: 16px;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 4px;
  background: var(--rr-color-surface);
  transition: all 120ms ease;
}

.rr-checkbox-wrapper:hover:not(.is-disabled) .rr-checkbox__inner {
  border-color: var(--rr-color-primary);
}

.rr-checkbox-wrapper.is-checked .rr-checkbox__inner {
  border-color: var(--rr-color-primary);
  background: var(--rr-color-primary);
}

.rr-checkbox-wrapper.is-checked .rr-checkbox__inner::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 4px;
  height: 8px;
  border: 2px solid #ffffff;
  border-top: 0;
  border-left: 0;
  transform: translate(-50%, -60%) rotate(45deg);
  content: "";
}

.rr-checkbox-wrapper.is-indeterminate .rr-checkbox__inner {
  border-color: var(--rr-color-primary);
  background: var(--rr-color-primary);
}

.rr-checkbox-wrapper.is-indeterminate .rr-checkbox__inner::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 2px;
  background: #ffffff;
  transform: translate(-50%, -50%);
  content: "";
}

.rr-checkbox-wrapper.is-disabled .rr-checkbox__inner {
  background: var(--rr-color-surface-subtle);
  border-color: var(--rr-color-border);
}
</style>
